import { failHard, failLoud } from '@resource-kit/core';
import { resolveWidget } from '@resource-kit/core';
import type { NamedWidgetEntry, WidgetCatalog, WidgetRequest } from '@resource-kit/core';
import type { WidgetName } from '@resource-kit/core';
import type { Widget, WidgetFor } from '@resource-kit/model';
import type { ValidatableField } from '../form/validation';

/**
 * The augmentable widget catalog (#1802), moved out of the host in #2167 PR-3.
 *
 * Core keeps what is genuinely representation-independent — the widget NAMES
 * (`WidgetRegistry`), the resolution order and the failure policy — and this
 * module binds them to the model's `Widget` shape and owns the catalog STATE.
 *
 * The three levels are unchanged: `item.widget` → `field.widget` → the
 * per-`FieldType` default. What changed is where level 3 comes from. It used to
 * be initialised at import time from `@lib/field-type-registry`, which is a
 * host module and therefore not something a package may name; now the HOST
 * SEEDS it through `seedFieldTypeDefaults` (ADR-0035 D4). Level 3 is still
 * CLOSED — it is exactly the field-type set, and nothing registers into it
 * piecemeal — it is simply installed from outside rather than imported.
 *
 * Levels 1 and 2 — named widgets — stay open, and their registration stays
 * EXPLICIT (design §5.2 discipline 2): features export `registerXxxWidgets()`
 * and the composition root calls them through `src/features/widgets.ts`. A bare
 * `import './widgets'` would leave the registration order to import order and
 * would be dropped by the bundler as a pure side-effect import.
 *
 * WHICH SEAM, AND WHY TWO: root-half consumers (`toFilterType`,
 * `validateFieldValue`) are framework-free and cannot call a React hook, so
 * they read this module-level singleton, which the composition root must seed.
 * React-half consumers take host facts through the DI seam instead
 * (`format.timeZone`, required at install time —
 * `packages/resource-kit-core/services.ts::SERVICE_MEMBERS.format`). The singleton
 * trades that seam's
 * install-time guarantee for a boot-order one.
 */
const namedWidgets = new Map<string, NamedWidgetEntry<Widget>>();

/**
 * Level 3, EMPTY until a host seeds it. Nothing here degrades to a default: a
 * read before the seed lands means the composition root is mis-wired, and both
 * of this module's READ entry points guard for it before answering — both on
 * the single `seed` witness below — so the mistake is reported where it was
 * made rather than rendering a field with no widget.
 *
 * This map is deliberately NOT that witness. It accumulates across seeds (see
 * `seedFieldTypeDefaults`), so "non-empty" only says somebody seeded SOMETHING,
 * which is not the precondition either reader needs. Whether THIS type is
 * covered is a second, narrower question, and `resolveFieldWidget` asks it
 * separately.
 */
const typeDefaultWidgets = new Map<string, Widget>();

/**
 * The one witness of "a host installed its field-type defaults", and the other
 * half of the seed. `Widget.zodBase` is a bare `ZodType`, but the default
 * validation schema for a field type is a FUNCTION of the field — the `enum`
 * type derives its schema from that field's own option list — so it cannot ride
 * on the type-default widget and arrives as its own member.
 *
 * Keeping the whole seed rather than a boolean puts ONE fact in ONE place: the
 * widget path and the schema path cannot answer differently about a mis-wired
 * composition root, and `null` narrows for the `zodBaseFor` call below without
 * a second, separately-drifting check.
 */
let seed: FieldTypeDefaults | null = null;

const catalog: WidgetCatalog<Widget> = {
  named: namedWidgets,
  typeDefaults: typeDefaultWidgets,
};

/**
 * What a host has to hand over so the catalog can answer at all (ADR-0035 D4).
 */
export interface FieldTypeDefaults {
  /** The per-`FieldType` default widget — level 3 of the resolution order. */
  readonly widgets: ReadonlyMap<string, Widget>;
  /** The per-`FieldType` default validation schema, derived from the field. */
  readonly zodBaseFor: (field: ValidatableField) => Widget['zodBase'];
}

/**
 * Install the host's field-type defaults. Called once from the composition
 * root's `registerFeatureWidgets()`, before the tree mounts.
 *
 * IDEMPOTENT PER TYPE: re-seeding replaces the entry for each type it names and
 * leaves the others alone. The map is deliberately NOT cleared first — a second
 * seed is a re-installation, not a redefinition of what the closed set contains,
 * and clearing would let a partial seed silently shrink it. A seed may therefore
 * be PARTIAL, which is why "seeded at all" and "this type is covered" are two
 * facts and get two checks in `resolveFieldWidget`.
 *
 * PER-TYPE COVERAGE IS CHECKED AT BOTH ENTRY POINTS (#2179), against this same
 * map. What is still not merged per type is `zodBaseFor` ITSELF: it is one
 * function, and `seed = defaults` REPLACES it wholesale on every call. So a
 * partial re-seed has to hand over a `zodBaseFor` answering for every type
 * seeded so far, or the schema entry point consults a narrower function than
 * the widget map it is paired with. The single production call point passes the
 * full catalog every time (`src/features/widgets.ts`), so nothing depends on
 * the difference today.
 */
export function seedFieldTypeDefaults(defaults: FieldTypeDefaults): void {
  for (const [type, widget] of defaults.widgets) typeDefaultWidgets.set(type, widget);
  seed = defaults;
}

/**
 * Register a named widget. `N` is constrained to the declared widget names, so
 * a typo is a compile error, and the second argument is derived FROM `N`, so
 * the registered metadata cannot claim a field type or mode the row never
 * declared.
 */
export function registerWidget<N extends WidgetName>(name: N, widget: WidgetFor<N>): void {
  if (namedWidgets.has(name)) {
    failLoud(`widget "${name}" is registered twice`);
    return;
  }
  namedWidgets.set(name, { widget, supportedTypes: widget.supportedTypes });
}

/**
 * Resolve the widget a field renders and edits with, complete: `item.widget` →
 * `field.widget` → type default, then every omitted projection filled from the
 * type default.
 *
 * The unseeded case is caught HERE, before delegating, because core cannot
 * catch it uniformly: `selectWidget` returns a named widget without consulting
 * the type default (`packages/resource-kit-core/widgets.ts::selectWidget`), and
 * `withTypeDefaultProjections` then hands it back unchanged when there is no
 * type default to borrow from
 * (`packages/resource-kit-core/widgets.ts::withTypeDefaultProjections`). So
 * on an unseeded catalog the named branch would answer a widget with every
 * borrowed projection missing while the unnamed branch failed loud — two
 * behaviours for one missing seed.
 *
 * TWO facts, one witness each, so neither diagnostic can name the wrong
 * mistake. "Did a host seed at all" is `seed` — the SAME witness
 * `resolveTypeZodBase` reads, so the two entry points cannot disagree about a
 * mis-wired composition root. "Is THIS type covered" is
 * `typeDefaultWidgets.has`, asked separately because a seed may legally be
 * partial (see `seedFieldTypeDefaults`) and a partial seed would otherwise let
 * the named branch answer an un-backfilled widget again. For a host that seeds
 * from `FIELD_TYPE_WIDGETS` the second check cannot fire: that map is total over
 * `FieldType`, and every `WidgetRequest.type` in the tree comes from a
 * `FieldType`-typed member.
 */
export function resolveFieldWidget(request: WidgetRequest): Widget {
  if (seed === null) {
    return failHard(
      'the field-type defaults have not been seeded; the composition root must call ' +
        'seedFieldTypeDefaults() before anything resolves a widget',
    );
  }
  if (!typeDefaultWidgets.has(request.type)) {
    return failHard(`no field-type default is seeded for "${request.type}"`);
  }
  return resolveWidget(catalog, request);
}

/**
 * The field type's default validation schema — layer 1 of `validateFieldValue`
 * when the field declares no `schema` of its own.
 *
 * Fails hard rather than answering `undefined` on an unseeded registry, because
 * the two are indistinguishable to the caller and mean opposite things: a field
 * type legitimately has no default schema (most of them do not), while an
 * unseeded registry means EVERY field silently lost its validation.
 *
 * TWO facts, one witness each, exactly as in `resolveFieldWidget` and from the
 * same two witnesses, so the entry points cannot answer differently about the
 * same catalog. The per-type half is #2179's, added for SYMMETRY with
 * `resolveFieldWidget` rather than to close an observed gap: `field.type` is
 * `ValidatableField['type']`, which is `ModelField['type']`, i.e. `FieldType`,
 * and the shipped host seeds `TYPE_WIDGET_COMPONENTS` and `FIELD_TYPE_REGISTRY`
 * as `satisfies Record<FieldType, …>` — both total — so no production
 * `ValidatableField` can name a type either map omits.
 *
 * WHAT THE PER-TYPE CHECK WITNESSES is `typeDefaultWidgets`, which
 * `seedFieldTypeDefaults` fills from `defaults.widgets` — NOT `zodBaseFor`,
 * which this function actually calls. A host that seeds a total `widgets` map
 * alongside a `zodBaseFor` that is missing an entry would still pass this
 * check; the only place that gap is exercised is `widget-seeding.test.ts`,
 * which seeds a PARTIAL map on purpose to prove the per-type message fires
 * before `zodBaseFor` is ever called.
 */
export function resolveTypeZodBase(field: ValidatableField): Widget['zodBase'] {
  if (seed === null) {
    return failHard(
      'the field-type defaults have not been seeded; the composition root must call ' +
        'seedFieldTypeDefaults() before anything validates a field',
    );
  }
  if (!typeDefaultWidgets.has(field.type)) {
    return failHard(`no field-type default is seeded for "${field.type}"`);
  }
  return seed.zodBaseFor(field);
}

/**
 * Test seam — the NAMED catalog is module state, so suites must start from
 * empty. The type defaults are NOT cleared: they are the closed field-type set
 * the host seeded, not something a suite registered, and a catalog without them
 * can only `failHard`.
 */
export function resetWidgetRegistry(): void {
  namedWidgets.clear();
}
